import React, { useState, useEffect } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { TeamMetrics } from '@/components/workforce/TeamMetrics'
import { DirectReportsCard } from '@/components/workforce/DirectReportsCard'
import { HierarchyTreeView } from '@/components/workforce/HierarchyTreeView'
import { ReassignManagerModal } from '@/components/workforce/ReassignManagerModal'
import { EmployeeAutocomplete } from '@/components/workforce/EmployeeAutocomplete'
import { EmployeeAvatar } from '@/components/vacation/EmployeeAvatar'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { MyTeamSkeleton } from '@/components/common/MyTeamSkeleton'
import { EmptyState } from '@/components/ui/EmptyState'
import { SensitiveText } from '@/components/privacy/SensitiveText'
import {
  getMyManagementTree,
  getMyAllReports,
  updateEmployee,
  getEmployee,
  EmployeeTree
} from '@/api/workforce'
import { Employee } from '@/types/workforce'
import {
  Users,
  List,
  Network,
  Download,
  UserCircle,
  Building2,
  Mail,
  Phone,
  Briefcase,
  Calendar,
  X,
  RefreshCw,
  Search
} from 'lucide-react'

type ViewMode = 'list' | 'tree' | 'search'

export default function MyTeamPage() {
  const { user } = useAuth()

  // Determine if user should use search mode (RH, analista-rh, vice-presidente)
  const shouldUseSearchMode = user?.role === 'rh' || user?.role === 'analista-rh' || user?.role === 'vice-presidente'

  const [viewMode, setViewMode] = useState<ViewMode>(shouldUseSearchMode ? 'search' : 'list')
  const [directReports, setDirectReports] = useState<Employee[]>([])
  const [managementTree, setManagementTree] = useState<EmployeeTree | null>(null)
  const [allReports, setAllReports] = useState<Employee[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [terminatedCount, setTerminatedCount] = useState(0)

  // Modal states
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null)
  const [employeeToReassign, setEmployeeToReassign] = useState<Employee | null>(null)
  const [currentManagerForModal, setCurrentManagerForModal] = useState<Employee | undefined>(undefined)

  useEffect(() => {
    loadTeamData()
  }, [])

  const loadTeamData = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true)
      } else {
        setLoading(true)
      }
      setError(null)

      const [directReportsData, treeData, allReportsData] = await Promise.all([
        getMyAllReports({ limit: 1000 }), // Use all-reports for direct reports too
        getMyManagementTree({ includeManager: true }),
        getMyAllReports({ limit: 1000 })
      ])

      console.log('🌳 [MyTeam] Management Tree Data:', treeData)
      console.log('🌳 [MyTeam] Has reports?', treeData?.reports?.length)
      console.log('🌳 [MyTeam] Direct Reports Count:', directReportsData.items.length)

      // FALLBACK: If management tree doesn't have reports but we have direct reports,
      // build a simple tree structure
      let finalTreeData = treeData
      if ((!treeData?.reports || treeData.reports.length === 0) && directReportsData.items.length > 0) {
        console.log('⚠️ [MyTeam] Management tree has no reports, building from direct reports list')
        finalTreeData = {
          ...treeData,
          reports: directReportsData.items as EmployeeTree[]
        }
      }

      // FIX: Enrich root node with current user's data if missing personalInfo
      if (finalTreeData && user?.employee) {
        console.log('🔍 [MyTeam] Current user employee data:', user.employee)
        console.log('🔍 [MyTeam] Tree root data before enrichment:', {
          hasPersonalInfo: !!finalTreeData.personalInfo,
          hasCurrentEngagement: !!finalTreeData.currentEngagement,
          roleName: finalTreeData.currentEngagement?.roleName
        })

        if (!finalTreeData.personalInfo?.name || !finalTreeData.currentEngagement?.roleName) {
          console.log('✨ [MyTeam] Enriching root node with user data')

          // If tree data is missing info, try to fetch current user's full employee record
          try {
            const tenantDomain = user.employee.tenantId || 'monumenta'
            const myEmployeeData = await getEmployee(tenantDomain, user.employee.employeeId)
            console.log('✨ [MyTeam] Fetched current user employee data:', myEmployeeData)

            finalTreeData = {
              ...finalTreeData,
              personalInfo: myEmployeeData.personalInfo || user.employee.personalInfo || finalTreeData.personalInfo,
              email: myEmployeeData.email || user.employee.email || finalTreeData.email,
              currentEngagement: myEmployeeData.currentEngagement || user.employee.currentEngagement || finalTreeData.currentEngagement,
              status: myEmployeeData.status || user.employee.status || finalTreeData.status || 'ACTIVE'
            }
          } catch (fetchError) {
            console.warn('⚠️ [MyTeam] Could not fetch current user employee data, using auth context:', fetchError)
            // Fallback to auth context data
            finalTreeData = {
              ...finalTreeData,
              personalInfo: user.employee.personalInfo || finalTreeData.personalInfo,
              email: user.employee.email || finalTreeData.email,
              currentEngagement: user.employee.currentEngagement || finalTreeData.currentEngagement,
              status: user.employee.status || finalTreeData.status || 'ACTIVE'
            }
          }
        }
      }

      // Filter out terminated employees from display lists (keep for "Desligados" metric)
      const activeDirectReports = directReportsData.items.filter(
        (emp: Employee) => emp.status?.toUpperCase() !== 'TERMINATED'
      )
      const activeAllReports = allReportsData.items.filter(
        (emp: Employee) => emp.status?.toUpperCase() !== 'TERMINATED'
      )
      const terminatedCount = allReportsData.items.filter(
        (emp: Employee) => emp.status?.toUpperCase() === 'TERMINATED'
      ).length

      setDirectReports(activeDirectReports)
      setManagementTree(finalTreeData)
      setAllReports(activeAllReports)
      setTerminatedCount(terminatedCount)
    } catch (err) {
      console.error('❌ Error loading team data:', err)
      setError(err instanceof Error ? err.message : 'Erro ao carregar dados da equipe')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  const handleViewDetails = (employee: Employee | EmployeeTree) => {
    setSelectedEmployee(employee as Employee)
  }

  const handleReassign = async (employee: Employee) => {
    setEmployeeToReassign(employee)

    // Manager relationship not currently stored in engagement model
    // For now, just show modal without pre-selected manager
    setCurrentManagerForModal(undefined)
  }

  const handleReassignConfirm = async (newManagerId: string | null) => {
    if (!employeeToReassign || !employeeToReassign.currentEngagement) return

    try {
      const tenantDomain = user?.employee?.tenantId || 'monumenta'
      await updateEmployee(tenantDomain, employeeToReassign.employeeId, {
        employment: {
          departmentId: Number(employeeToReassign.currentEngagement.departmentId || 0),
          roleId: Number(employeeToReassign.currentEngagement.roleId || 0),
          managerId: newManagerId,
          startDate: employeeToReassign.currentEngagement.startDate,
          endDate: employeeToReassign.currentEngagement.endDate || undefined,
          status: employeeToReassign.currentEngagement.status,
          contractType: employeeToReassign.currentEngagement.contractType
        }
      })

      // Reload data
      await loadTeamData(true)

      // Close modal
      setEmployeeToReassign(null)
    } catch (error) {
      console.error('Error reassigning manager:', error)
      throw error
    }
  }

  const formatDate = (date: string) => {
    if (!date) return 'Data não informada'

    const dateObj = new Date(date)
    if (isNaN(dateObj.getTime())) return 'Data inválida'

    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(dateObj)
  }

  const getStatusLabel = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'ACTIVE':
        return 'Ativo'
      case 'INACTIVE':
        return 'Inativo'
      case 'TERMINATED':
        return 'Demitido'
      case 'PENDING':
        return 'Pendente'
      default:
        return status
    }
  }

  // Calculate average tenure (in years)
  const calculateAvgTenure = (): number | undefined => {
    if (!allReports || allReports.length === 0) return undefined

    const totalDays = allReports.reduce((sum, emp) => {
      if (!emp.currentEngagement?.startDate) return sum

      const startDate = new Date(emp.currentEngagement.startDate)
      const today = new Date()

      // Validate date
      if (isNaN(startDate.getTime())) return sum

      const diffTime = Math.abs(today.getTime() - startDate.getTime())
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
      return sum + diffDays
    }, 0)

    const avgDays = totalDays / allReports.length
    const avgYears = avgDays / 365

    // Return undefined if calculation resulted in invalid number
    return isNaN(avgYears) ? undefined : avgYears
  }

  if (loading) {
    return <MyTeamSkeleton />
  }

  if (error) {
    return (
      
        <EmptyState
          icon={Users}
          title="Erro ao Carregar Equipe"
          description={error}
          action={
            <Button onClick={() => loadTeamData()}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Tentar Novamente
            </Button>
          }
        />
      
    )
  }

  return (
    <>
      <div className="space-y-6 animate-fade-in-up">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold text-white mb-2">
              Minha Equipe
            </h1>
            <p className="font-body text-brand-gray">
              Gerencie seus subordinados diretos e visualize sua hierarquia
            </p>
          </div>
          <div className="mt-4 sm:mt-0 flex space-x-3">
            {user && (user.role === 'admin' || user.role === 'rh' || user.role === 'analista-rh' || user.role === 'vice-presidente') && (
              <Button variant="ghost" size="sm">
                <Download className="w-4 h-4 mr-2" />
                Exportar
              </Button>
            )}
          </div>
        </div>

        {/* Team Metrics */}
        <TeamMetrics
          directReportsCount={directReports.length}
          totalTeamSize={allReports.length}
          spanOfControl={directReports.length}
          avgTenure={calculateAvgTenure()}
          terminatedCount={terminatedCount}
        />

        {/* View Mode Toggle */}
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-lg font-semibold text-white">
              {viewMode === 'search' ? 'Buscar Colaboradores' : viewMode === 'list' ? 'Subordinados Diretos' : 'Hierarquia Completa'}
            </h2>
            <div className="flex gap-2">
              {shouldUseSearchMode && (
                <Button
                  variant={viewMode === 'search' ? 'primary' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('search')}
                >
                  <Search className="w-4 h-4 mr-2" />
                  Buscar
                </Button>
              )}
              {!shouldUseSearchMode && (
                <>
                  <Button
                    variant={viewMode === 'list' ? 'primary' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('list')}
                  >
                    <List className="w-4 h-4 mr-2" />
                    Lista
                  </Button>
                  <Button
                    variant={viewMode === 'tree' ? 'primary' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('tree')}
                  >
                    <Network className="w-4 h-4 mr-2" />
                    Hierarquia
                  </Button>
                </>
              )}
            </div>
          </div>
        </Card>

        {/* Main Content */}
        {viewMode === 'search' ? (
          <Card className="p-6">
            <div className="space-y-4">
              <p className="text-brand-gray mb-4">
                Use a busca abaixo para encontrar qualquer colaborador da empresa por nome ou email.
              </p>
              <EmployeeAutocomplete
                placeholder="Buscar colaborador por nome ou email..."
                navigateOnSelect={false}
                onSelect={async (employeeId) => {
                  try {
                    const tenantDomain = user?.employee?.tenantId || 'monumenta'
                    const employeeData = await getEmployee(tenantDomain, employeeId)
                    setSelectedEmployee(employeeData)
                  } catch (err) {
                    console.error('Error fetching employee:', err)
                  }
                }}
              />
            </div>
          </Card>
        ) : viewMode === 'list' ? (
          <DirectReportsCard
            reports={directReports}
            onViewDetails={handleViewDetails}
            onReassign={handleReassign}
          />
        ) : managementTree ? (
          <HierarchyTreeView
            managementTree={managementTree}
            onEmployeeClick={handleViewDetails}
          />
        ) : (
          <EmptyState
            icon={Network}
            title="Sem Dados de Hierarquia"
            description="Não foi possível carregar a árvore hierárquica."
          />
        )}
      </div>

      {/* Employee Detail Modal */}
      {selectedEmployee && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 animate-fade-in">
          <Card className="max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="font-display text-2xl font-bold text-white">
                  Detalhes do Colaborador
                </h2>
                <button
                  onClick={() => setSelectedEmployee(null)}
                  className="text-brand-gray hover:text-white transition-colors duration-200"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="space-y-6">
                {/* Employee Header */}
                <div className="flex items-center space-x-6">
                  <EmployeeAvatar
                    src={selectedEmployee.personalInfo?.pictureUrl}
                    name={selectedEmployee.personalInfo?.name || selectedEmployee.email || 'Desconhecido'}
                    size="xl"
                  />
                  <div>
                    <h3 className="font-display text-xl font-bold text-white">
                      <SensitiveText type="name">
                        {selectedEmployee.personalInfo?.name || selectedEmployee.email || 'Desconhecido'}
                      </SensitiveText>
                    </h3>
                    <p className="text-lg text-brand-gray">
                      {selectedEmployee.currentEngagement?.roleName || 'Função não definida'}
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-success/20 text-success border border-success/40">
                        {getStatusLabel(selectedEmployee.status || 'ACTIVE')}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Contact Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center space-x-3">
                    <Mail className="w-5 h-5 text-brand-gray flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm text-brand-gray">Email</p>
                      <p className="font-medium text-white truncate">
                        <SensitiveText type="email">
                          {selectedEmployee.email}
                        </SensitiveText>
                      </p>
                    </div>
                  </div>
                  {selectedEmployee.personalInfo?.phone && (
                    <div className="flex items-center space-x-3">
                      <Phone className="w-5 h-5 text-brand-gray flex-shrink-0" />
                      <div>
                        <p className="text-sm text-brand-gray">Telefone</p>
                        <p className="font-medium text-white">
                          <SensitiveText type="phone">
                            {selectedEmployee.personalInfo.phone}
                          </SensitiveText>
                        </p>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center space-x-3">
                    <Building2 className="w-5 h-5 text-brand-gray flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm text-brand-gray">Departamento</p>
                      <p className="font-medium text-white truncate">
                        {selectedEmployee.currentEngagement?.departmentName || 'N/A'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Briefcase className="w-5 h-5 text-brand-gray flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm text-brand-gray">Tipo de Contrato</p>
                      <p className="font-medium text-white">
                        {selectedEmployee.currentEngagement?.contractType || 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Employment Info */}
                <div>
                  <h4 className="font-display font-semibold text-white mb-3">
                    Informações de Emprego
                  </h4>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-brand-gray" />
                      <span className="text-brand-gray">Data de Admissão:</span>
                      <span className="text-white">
                        {formatDate(selectedEmployee.currentEngagement?.startDate || '')}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-4 border-t border-gray-700/50">
                  {user && (user.role === 'admin' || user.role === 'rh' || user.role === 'analista-rh' || user.role === 'vice-presidente') && (
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setEmployeeToReassign(selectedEmployee)
                        setSelectedEmployee(null)
                      }}
                      className="flex-1"
                    >
                      Reatribuir Gestor
                    </Button>
                  )}
                  <Button
                    variant="primary"
                    onClick={() => setSelectedEmployee(null)}
                    className="flex-1"
                  >
                    Fechar
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Reassign Manager Modal */}
      {employeeToReassign && (
        <ReassignManagerModal
          employee={employeeToReassign}
          currentManager={currentManagerForModal}
          onConfirm={handleReassignConfirm}
          onCancel={() => {
            setEmployeeToReassign(null)
            setCurrentManagerForModal(undefined)
          }}
        />
      )}
    </>
  )
}
